<template>
  <div
    class="audience-group-node"
    :class="{ first: isRoot }"
    @dragover="hoveredNode = null">
    <div class="header">
      <div
        v-if="!isRoot"
        class="audience-node-drag flex pl-2 pr-4 py-4">
        <span
          class="icon"
          style="width: 10px">
          <icon-drag />
        </span>
      </div>
      <span
        v-if="isNegated"
        class="badge badge-danger mr-4">
        NOT
      </span>
      <template v-if="isEditable">
        <div
          v-if="!isRoot"
          class="actions">
          <div class="mr-4">
            <multiselect
              v-model="operator"
              data-testid="operator_toggle"
              class="logical-operators"
              :options="operatorTypes"
              :disabled="hasMastercardSegment"
              track-by="value"
              label="label"
              :show-labels="false"
              placeholder="" />
          </div>
          <div class="toggle danger">
            <toggle-button
              :value="isNegated"
              :sync="true"
              @change="toggleNot()" />
            <span>{{ capitalize(i18n.t('actions.avoid')) }}</span>
          </div>
          <audience-logic-node-action-buttons
            button-size="btn-xs"
            :show-buttons-only="innerButtons"
            @add-group="addGroup"
            @add-rule="addRule"
            @remove-group="removeGroup"
            @delete-group="deleteGroup" />
        </div>
        <div
          v-else-if="childNodes.length"
          class="actions">
          <div class="mr-4">
            <multiselect
              v-model="operator"
              data-testid="operator_toggle"
              class="logical-operators mb-0"
              :options="operatorTypes"
              :disabled="hasMastercardSegment"
              track-by="value"
              label="label"
              :show-labels="false"
              placeholder="" />
          </div>
          <audience-logic-node-action-buttons
            button-size="btn-sm"
            :buttons="commonButtons"
            :show-buttons-only="commonButtons"
            @add-group="addGroup"
            @add-rule="addRule"
            @remove-group="removeGroup"
            @delete-group="deleteGroup" />
        </div>
      </template>
    </div>
    <draggable
      v-model="childNodes"
      tag="ul"
      item-key="key"
      handle=".audience-node-drag"
      ghost-class="ghost"
      drag-class="drag"
      :empty-insert-threshold="50"
      :swap-threshold="0.2"
      :group="{ put: true }"
      @add="onDragAdd"
      @start="onDragStart"
      @end="onDragEnd">
      <template #item="{ element, index }">
        <li>
          <audience-logic-node
            :ref="addChildRef"
            class="audience-logic-node"
            :class="{ dragover: hoveredNode === element && index !== dragIndex && !parent?.parent }"
            :parent="group"
            :is-editable="isEditable"
            :geofence-groups="geofenceGroups"
            :selected-data-provider="selectedDataProvider"
            :type="element.type"
            :node="element"
            :inline-popups="inlinePopups"
            @dragover="() => (hoveredNode = element)"
            @dragend="hoveredNode = null"
            @before-destroy="
              (childRef) => {
                removeChildRef(childRef);
              }
            " />
          <div
            v-if="index + 1 < childNodes.length && (index + 1 !== dragIndex || index + 2 < childNodes.length)"
            class="logical-operators badge badge-info"
            @dragover="hoveredNode = null">
            <span v-if="operator.value === '&'">{{ uppercase($t('conjuctions.and')) }}</span>
            <span v-if="operator.value === '|'">{{ uppercase($t('conjuctions.or')) }}</span>
          </div>
        </li>
      </template>
    </draggable>
    <div class="footer">
      <div
        v-if="isRoot && isEditable && childNodes.length"
        class="actions">
        <audience-logic-node-action-buttons
          button-size="btn-sm"
          :show-buttons-only="commonButtons"
          :data-provider="selectedDataProvider"
          @add-group="addGroup"
          @add-rule="addRule"
          @remove-group="removeGroup"
          @delete-group="deleteGroup" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { capitalize, computed, ref, watch } from 'vue';
  import { useI18n } from 'vue-i18n';
  import Draggable from 'vuedraggable';

  import ToggleButton from '@/common/components/controls/ToggleButton.vue';
  import { AudienceLogicTypes } from '@/common/enums/audience-logic-types';
  import { AudienceRuleButtons } from '@/common/enums/audience-rule-buttons.enum';
  import { AudienceRuleModes } from '@/common/enums/audience-rule-modes';
  import { DataMarketplaceCodes } from '@/common/enums/data-marketplace-codes.enum';
  import { DataProviderCodes, isLifesightDataProvider } from '@/common/enums/data-provider-codes';
  import { IconDrag } from '@/common/icons/monochrome';
  import { AudienceLogic, GeofenceGroup } from '@/common/models';
  import AudienceGroup, { operatorTypes } from '@/common/models/audience/AudienceGroup';
  import AudienceNode from '@/common/models/audience/AudienceNode';
  import AudienceRule from '@/common/models/audience/AudienceRule';
  import GlobalEventBus from '@/GlobalEventBus';
  import GlobalEvents from '@/GlobalEvents';
  import { uppercase, useInjected } from '@/globals';
  import AudienceLogicNode from '@/org-app/components/audienceLogics/AudienceLogicNode.vue';
  import AudienceLogicNodeActionButtons from '@/org-app/components/audienceLogics/AudienceLogicNodeActionButtons.vue';
  import { useDataProviders } from '@/org-app/services/tanstack/queries';
  import { useDataMarketplaces } from '@/org-app/services/tanstack/queries/dataMarketplaces.queries';
  import { mapActions, mapOrgAppState } from '@/utils/StateMapping';

  //
  // Setup
  //
  const { updateNodes, updateAudienceRuleIsNegated, updateNodeOperator } = mapActions();
  const i18n = useI18n();
  const props = withDefaults(
    defineProps<{
      geofenceGroups: GeofenceGroup[];
      isEditable: boolean;
      group: AudienceGroup;
      inlinePopups: boolean;
      parent?: AudienceGroup;
    }>(),
    {
      inlinePopups: false,
      isEditable: false,
      parent: null,
    },
  );
  const emits = defineEmits<{
    (e: 'ruleChanged', val: AudienceGroup): void;
    (e: 'negationChanged', val: AudienceGroup): void;
  }>();
  const { items: dataMarketplaces } = useDataMarketplaces();
  const { items: dataProviders } = useDataProviders();
  const audienceLogic = useInjected<AudienceLogic>('audienceLogic');
  const { organizationSettings } = mapOrgAppState();

  // Constants
  const childRefs = [];

  // Computed
  const selectedDataProvider = computed(() =>
    dataProviders.value.find((dp) => {
      return dp.id === audienceLogic.value.dataProviderID;
    }),
  );
  const selectedDataMarketplace = computed(() =>
    dataMarketplaces.value.find((dm) => dm.id === audienceLogic.value.dataMarketplaceID),
  );

  // Data
  const dragIndex = ref<number>(-1);
  const hoveredNode = ref<AudienceRule>(null);
  const isNegated = ref<boolean>(props.group.isNegated ?? false);
  const operator = computed({
    get() {
      return props.group.operator || operatorTypes[1];
    },
    set(value) {
      updateNodeOperator({
        entity: props.group,
        value,
      });
      GlobalEventBus.$emit(GlobalEvents.audienceLogicExpressionChanged);
    },
  });
  const isRoot = computed(() => props.parent === null);
  const childNodes = computed<AudienceNode[]>({
    get() {
      return [...props.group.nodes];
    },
    set(value) {
      updateNodes({
        entity: props.group,
        value,
      });
    },
  });
  const externalSegmentButton = computed(() => {
    if (selectedDataMarketplace.value) {
      const dpMarketplaceID = selectedDataMarketplace.value.id;
      const matchingMarketplaceCode = dataMarketplaces.value.find((dm) => dm.id === dpMarketplaceID)?.code;
      if (matchingMarketplaceCode === DataMarketplaceCodes.LIVERAMP) {
        return AudienceRuleButtons.Liveramp;
      }
    } else if (selectedDataProvider.value) {
      const dpCode = selectedDataProvider.value.code;
      switch (dpCode) {
        case DataProviderCodes.ADSQUARE:
          return AudienceRuleButtons.Adsquare;
        case DataProviderCodes.EPSILON:
          return AudienceRuleButtons.Epsilon;
        case DataProviderCodes.MASTERCARD:
          return AudienceRuleButtons.Mastercard;
        default:
          return AudienceRuleButtons.Predefined;
      }
    } else if (dataProviders.value.some(isLifesightDataProvider)) {
      // Otherwise we default to lifesight
      return AudienceRuleButtons.Predefined;
    }
    return null;
  });
  const externalSegmentsPresent = computed(() => {
    return props.group.topParent.hasSegmentRule;
  });
  const hasMastercardSegment = computed(() => {
    return externalSegmentsPresent.value && selectedDataProvider.value?.code === DataProviderCodes.MASTERCARD;
  });
  const showGeofenceGroup = computed(() => organizationSettings.uiEnableGeofenceGroupsRulesInAudienceLogics);
  const showExternalDemographic = computed(() => organizationSettings.uiEnableCustomAudienceExternalDemographics);
  const baseButtons = computed(() => {
    const buttons = [AudienceRuleButtons.DeviceID, AudienceRuleButtons.Demographic];
    if (showExternalDemographic.value) {
      buttons.push(AudienceRuleButtons.Demo);
    }
    if (showGeofenceGroup.value) {
      buttons.push(AudienceRuleButtons.Geofence);
    }
    return buttons;
  });
  const commonButtons = computed(() => {
    const buttons = [];

    // Mastercard segments are very limited, dont support grouping
    if (!props.parent?.parent && !hasMastercardSegment.value) {
      buttons.push(AudienceRuleButtons.Group);
    }

    if (externalSegmentButton.value) {
      buttons.push(externalSegmentButton.value);
    }

    if (!externalSegmentsPresent.value) {
      buttons.push(...baseButtons.value);
    } else if (selectedDataProvider.value) {
      if (selectedDataProvider.value.code === DataProviderCodes.EPSILON) {
        // Special case for epsilon, works with geofence
        buttons.push(AudienceRuleButtons.Geofence);
      } else if (isLifesightDataProvider(selectedDataProvider.value)) {
        // Special case for lifesight, works with everything
        buttons.push(...baseButtons.value);
      }
    }

    return buttons;
  });
  const innerButtons = computed(() => {
    return [AudienceRuleButtons.Delete, AudienceRuleButtons.Ungroup, ...commonButtons.value];
  });

  // Exported
  defineExpose({
    deleteGroup,
    removeGroup,
    addRule,
    addGroup,
    childRefs,
    group: props.group,
  });

  function deleteGroup() {
    childRefs.forEach((child) => {
      child.deleteSelf();
    });

    if (!isRoot.value) {
      const parentNodes = props.parent.nodes;
      const parentIndex = parentNodes.indexOf(props.group);
      const newParentNodes = [...parentNodes];
      newParentNodes.splice(parentIndex, 1);
      updateNodes({
        entity: props.parent,
        value: newParentNodes,
      });
    }
    emits('ruleChanged', props.group);
  }

  function addRule(mode: AudienceRuleModes) {
    const rule = AudienceRule.fromObject({ mode, parent: props.group, dataProviderID: selectedDataProvider.value?.id });
    const newNodes = [...childNodes.value];
    newNodes.push(rule);
    updateNodes({
      entity: props.group,
      value: newNodes,
    });
    GlobalEventBus.$emit(GlobalEvents.addAudienceLogicRule, rule);
  }

  function addGroup(index?: number) {
    const group = new AudienceGroup([], props.group, false, props.group.operator.value === '&' ? '|' : '&');
    const newNodes = [...childNodes.value];
    if (index !== undefined) {
      newNodes.splice(index, 0, group);
    } else {
      newNodes.push(group);
    }
    updateNodes({
      entity: props.group,
      value: newNodes,
    });
    emits('ruleChanged', props.group);
    return group;
  }

  function removeGroup() {
    const indexInParent = props.parent.nodes.indexOf(props.group);
    const parentNodes = [...props.parent.nodes];
    parentNodes.splice(
      indexInParent,
      1,
      ...childNodes.value.map((n) =>
        n.type === AudienceLogicTypes.Rule
          ? AudienceRule.fromObject({
              ...n,
              isNegated: isNegated.value ? !(n as AudienceRule).isNegated : (n as AudienceRule).isNegated,
            })
          : n,
      ),
    );
    updateNodes({
      entity: props.parent,
      value: parentNodes,
    });
    emits('ruleChanged', props.group);
  }

  // Internal Methods
  function onDragStart(e) {
    // dragging started
    const { oldDraggableIndex } = e;
    dragIndex.value = oldDraggableIndex;
  }

  function onDragEnd(e) {
    const { to, from, newDraggableIndex } = e;
    // create new list
    if (to === from) {
      const rule = props.group.nodes[newDraggableIndex];
      groupNodes(hoveredNode.value as AudienceNode, rule);
    }
    hoveredNode.value = null;
    dragIndex.value = -1;
    GlobalEventBus.$emit(GlobalEvents.audienceLogicExpressionChanged);
  }

  function onDragAdd(e) {
    // addition to list
    const { newDraggableIndex } = e;
    const node = props.group.nodes[newDraggableIndex];
    node.parent = props.group;
    // create new list
    groupNodes(hoveredNode.value as AudienceNode, node);
    hoveredNode.value = null;
    dragIndex.value = -1;
    GlobalEventBus.$emit(GlobalEvents.audienceLogicExpressionChanged);
  }

  function groupNodes(first: AudienceNode, second: AudienceNode) {
    // Both need to be valid rules, no more depth than 2
    if (
      !props.group.parent?.parent &&
      first &&
      second &&
      first !== second &&
      first instanceof AudienceRule &&
      second instanceof AudienceRule
    ) {
      const index = Math.max(props.group.nodes.indexOf(first) - 1, 0);
      // Remove from this group
      childNodes.value = props.group.nodes.filter((n) => n !== first && n !== second);
      // Create group
      const newGroup = addGroup(index);
      // Add to new group
      newGroup.addChild(first, second);
      updateNodes({
        entity: newGroup,
        value: [first, second],
      });
      GlobalEventBus.$emit(GlobalEvents.audienceLogicExpressionChanged);
    }
  }

  function toggleNot() {
    isNegated.value = !props.group.isNegated;
    if (isNegated.value) {
      props.group.nodes.forEach((node) => {
        if (node.type === AudienceLogicTypes.Rule) {
          updateAudienceRuleIsNegated({ audienceRule: node, value: false });
        }
      });
    }
    emits('negationChanged', props.group);
  }

  function addChildRef(childRef) {
    if (childRef && !childRefs.includes(childRef)) {
      childRefs.push(childRef);
    }
  }

  function removeChildRef(childRef) {
    const refIndex = childRefs.indexOf(childRef);
    childRefs.splice(refIndex, 1);
  }

  watch(hasMastercardSegment, (val) => {
    // Switch to OR when we add mastercard
    if (val) {
      operator.value = operatorTypes[1];
    }
  });
</script>

<style lang="scss" scoped>
  @use '@/styles/variables' as *;

  .audience-node-drag {
    cursor: grab;
  }

  .logical-operators.badge {
    pointer-events: none;
    margin-bottom: 0;
  }

  .audience-group-node {
    background: var(--secondaryColorFaded95);
    border: solid 0.1rem $borders;
    flex-grow: 1;
    padding: 1.6rem;
    margin: 0;

    &.first {
      border-width: 0;
      background: transparent;
      padding: 0;

      > ul {
        margin-top: 24px;
      }
    }

    > .header,
    .footer {
      display: flex;
      flex-direction: row;
      align-items: center;
      margin-bottom: 1rem;

      .actions {
        display: flex;
        flex-grow: 1;
        align-items: center;
        flex-shrink: 1;
        flex-wrap: nowrap;

        button {
          margin-left: 1rem;
        }

        .toggle {
          margin-right: 1.6rem;
        }
      }
    }

    > ul {
      margin: 9px 24px 24px;
      padding: 0;
      display: flex;
      flex-direction: column;
      gap: 1.6rem;
      list-style: none;
      min-height: 4rem;

      li {
        padding: 0;

        &.ghost {
          opacity: 0.5;
        }

        &.ghost,
        &.drag {
          .logical-operators.badge {
            display: none;
          }
        }
      }

      > .footer {
        margin-top: 1rem;
      }
    }
  }

  .group-header {
    color: var(--secondaryColorReverse);
    padding: 0.8rem 1.6rem;
    text-transform: uppercase;
    font-weight: 900;
    font-size: 0.9em;
    border-radius: 0.4rem 0.4rem 0 0;
  }
</style>
